// The "Calls" tab of the Mail workspace (Session AUB): phone calls from callers not yet matched to a customer. This is the
// phone half of the old Enquiries page (Session ARM, D88), lifted out unchanged in behaviour: the same read
// (GET /api/call-logs?unmatched=true), the same three row actions, each reusing what already exists: Match
// (MatchCallModal, which sends the customer and nothing else), Raise work (the SAME AcceptCallAsWorkModal and
// RaiseWorkModal the customer Call log tab uses, carrying the call), and Delete (the existing soft delete, two-step).
// A call is not an email, so it never opens the review card.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../../lib/api";
import type { CallLogDto, RaisedWorkRef } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { filterRows, type SearchAccessors } from "../../../lib/useListSearch";
import { EmptyState } from "../../../components/EmptyState";
import { ListToolbar } from "../../../components/ListToolbar";
import { StatusPill } from "../../../components/StatusPill";
import { Loader } from "../../../components/Loader";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { raisedWorkLabel } from "../../../components/CallLogTable";
import { formatDateTime } from "../../../lib/format";
import { AcceptCallAsWorkModal, callerLine } from "../AcceptCallAsWorkModal";
import { RaiseWorkModal, raisedWorkMessage } from "../RaiseWorkModal";
import { MatchCallModal } from "../MatchCallModal";
import { phoneNeedsAction } from "../EnquiriesPage";

// Unmatched calls are read in one page, as the Enquiries page always did: at ELE's volume (a handful of unknown callers a
// week) 200 is far more than will ever be waiting, and the page says so if it is ever exceeded.
const PHONE_PAGE_SIZE = 200;
const ROW_ACTION = "font-menu text-xs tracking-wide transition hover:text-ele-strong max-sm:min-h-11";
const CALL_SEARCH_ACCESSORS: SearchAccessors<CallLogDto> = [(c) => c.callerName, (c) => c.callerPhone, (c) => c.topic];

function raisedWorkHref(work: RaisedWorkRef): string {
  return work.kind === "callout" ? "/office/callouts" : `/office/jobsheets?job=${encodeURIComponent(work.id)}`;
}

type CallFilter = "needs_action" | "all";

export function PhoneEnquiriesPanel() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallLogDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CallFilter>("needs_action");
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState<{ text: string; link?: { to: string; label: string } } | null>(null);
  const [matching, setMatching] = useState<CallLogDto | null>(null);
  const [accepting, setAccepting] = useState<CallLogDto | null>(null);
  const [raising, setRaising] = useState<CallLogDto | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.callLogs.list({ unmatched: true, pageSize: PHONE_PAGE_SIZE });
      setCalls(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(friendlyApiError(err, "The phone enquiries could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function removeCall(call: CallLogDto) {
    setDeletingId(call.id);
    setRowError(null);
    try {
      await api.callLogs.remove(call.id);
      setCalls((list) => list.filter((c) => c.id !== call.id));
      setTotal((n) => Math.max(0, n - 1));
      setNotice({ text: `The call from ${callerLine(call)} was deleted.` });
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not delete that call. Please try again."));
    } finally {
      setDeletingId(null);
    }
  }

  const rows = filterRows(
    calls.filter((c) => filter === "all" || phoneNeedsAction(c)),
    search,
    CALL_SEARCH_ACCESSORS,
  ).sort((a, b) => b.calledAt.localeCompare(a.calledAt));

  return (
    <div>
      <p className="mb-3 text-sm text-ele-body">
        Phone calls from callers not yet matched to a customer. Each one stays here until you match it to a customer, raise
        work from it, or delete it.
      </p>

      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice.text}
          {notice.link && (
            <>
              {" "}
              <Link to={notice.link.to} className="text-ele-green underline-offset-2 hover:underline">
                {notice.link.label}
              </Link>
            </>
          )}
        </p>
      )}
      {rowError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {rowError}
        </p>
      )}

      {!loading && !error && calls.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search caller, number or topic..."
          searchLabel="Search calls"
          filters={(
            [
              { value: "needs_action", label: "Needs action" },
              { value: "all", label: "All" },
            ] as const
          ).map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                filter === f.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {f.label}
            </button>
          ))}
        />
      )}

      {total > calls.length && !error && (
        <p className="mb-3 text-xs text-ele-body">
          Showing the {calls.length} most recent unmatched calls of {total}. Match or delete older ones to bring the rest into view.
        </p>
      )}

      {loading ? (
        <Loader label="Loading the calls..." rows={4} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <button type="button" onClick={() => void load()} className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:min-h-11">
            Retry
          </button>
        </div>
      ) : calls.length === 0 ? (
        <EmptyState message="No calls from unknown callers are waiting." hint="A call from someone not yet matched to a customer appears here as soon as it is logged." />
      ) : rows.length === 0 ? (
        <EmptyState message={search.trim() ? `No calls match "${search.trim()}".` : "No calls need action."} />
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "52%" }} />
              <col className="max-md:hidden" style={{ width: "22%" }} />
              <col style={{ width: "26%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Caller</th>
                <th className="max-md:hidden px-3 py-2">Called</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-ele-purple-accent" data-testid={`phone-enquiry-${c.id}`}>
                  <td className="cell-wrap px-3 py-2 align-top">
                    <div className="font-heading text-ele-strong">{c.callerName?.trim() || "Unknown caller"}</div>
                    <div className="mt-0.5 truncate text-xs text-ele-body opacity-80">
                      {[c.callerPhone, c.topic].filter((v) => v && v.trim().length > 0).join(" · ") || "No number or topic recorded"}
                    </div>
                    {(c.raisedWork ?? []).length > 0 && (
                      <ul className="mt-1 text-xs">
                        {(c.raisedWork ?? []).map((w) => (
                          <li key={`${w.kind}-${w.id}`}>
                            <Link to={raisedWorkHref(w)} className="text-ele-green underline-offset-2 hover:underline">
                              {raisedWorkLabel(w)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <button
                        type="button"
                        onClick={() => {
                          setNotice(null);
                          setRowError(null);
                          setMatching(c);
                        }}
                        className={`${ROW_ACTION} text-ele-blue`}
                      >
                        Match to a customer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNotice(null);
                          setRowError(null);
                          setAccepting(c);
                        }}
                        className={`${ROW_ACTION} text-ele-green`}
                      >
                        Raise work
                      </button>
                      <ConfirmButton
                        onConfirm={() => void removeCall(c)}
                        busy={deletingId === c.id}
                        className={`${ROW_ACTION} text-ele-body`}
                        title={`Delete the call from ${callerLine(c)}`}
                      />
                    </div>
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top">{formatDateTime(c.calledAt)}</td>
                  <td className="px-3 py-2 align-top">
                    {phoneNeedsAction(c) ? <StatusPill word="No work raised" tone="grey" /> : <StatusPill word="Work raised" tone="green" />}
                    <div className="mt-1 text-xs text-ele-body md:hidden">{formatDateTime(c.calledAt)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {matching && (
        <MatchCallModal
          call={matching}
          onClose={() => setMatching(null)}
          onMatched={(customer) => {
            const matchedId = matching.id;
            setMatching(null);
            // It is no longer an unknown caller, so it leaves this tab for the customer's own Call log tab.
            setCalls((list) => list.filter((x) => x.id !== matchedId));
            setTotal((n) => Math.max(0, n - 1));
            setNotice({
              text: `Matched to ${customer.name}. The call is now on their Call log tab.`,
              link: { to: `/office/customers/${encodeURIComponent(customer.id)}`, label: `Open ${customer.name}` },
            });
          }}
        />
      )}

      {accepting && (
        <AcceptCallAsWorkModal
          call={accepting}
          onClose={() => setAccepting(null)}
          onCallout={() => {
            navigate(`/office/callouts?${new URLSearchParams({ new: "log", callLogId: accepting.id }).toString()}`);
          }}
          onPlannedWork={() => {
            setRaising(accepting);
            setAccepting(null);
          }}
          onQuote={() => {
            navigate("/office/quotes/new", { state: { prefill: { customerName: accepting.callerName ?? "" } } });
            setAccepting(null);
          }}
        />
      )}

      {raising && (
        <RaiseWorkModal
          preset={{ type: "repair", callLogId: raising.id, callerLabel: callerLine(raising) }}
          onClose={() => setRaising(null)}
          onRaised={(result) => {
            setRaising(null);
            setNotice({ text: raisedWorkMessage(result) });
            void load();
          }}
        />
      )}
    </div>
  );
}
